// Single shared live price / 1D source for the Stock Analysis page.
// One fetch per ticker per 30s (module-level store, in-flight de-dup); every
// component that shows a price or 1D return subscribes here — none fetch their own.
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../config/api";

const TTL_MS = 30000;
const store = {}; // ticker -> { data, ts, promise, subs:Set }

function entry(t) {
  if (!store[t]) store[t] = { data: null, ts: 0, promise: null, subs: new Set(), error: null };
  return store[t];
}

export async function getLiveQuote(ticker, force = false) {
  const t = String(ticker || "").toUpperCase();
  if (!t) return null;
  const e = entry(t);
  if (!force && e.data && Date.now() - e.ts < TTL_MS) return e.data;
  if (e.promise) return e.promise;
  e.promise = axios.get(`${API}/stock/live-quote/${t}`, { timeout: 15000 })
    .then(r => { e.data = r.data; e.ts = Date.now(); e.error = null; return e.data; })
    .catch(err => { e.error = err.message; return e.data; })
    .finally(() => { e.promise = null; e.subs.forEach(fn => fn()); });
  return e.promise;
}

export function useLiveQuote(ticker, refreshMs = TTL_MS) {
  const t = String(ticker || "").toUpperCase();
  const [, bump] = useState(0);
  useEffect(() => {
    if (!t) return undefined;
    const e = entry(t);
    const fn = () => bump(n => n + 1);
    e.subs.add(fn);
    getLiveQuote(t);
    const id = setInterval(() => getLiveQuote(t), refreshMs);
    return () => { e.subs.delete(fn); clearInterval(id); };
  }, [t, refreshMs]);
  const refresh = useCallback(() => getLiveQuote(t, true), [t]);
  const e = t ? entry(t) : null;
  return { quote: e?.data || null, error: e?.error || null, loading: !!e?.promise && !e?.data, refresh };
}

export const fmtAsOf = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");

// Compact "source · time pulled" label every price / 1D display must carry.
export function LiveSourceLabel({ quote, testId, className = "" }) {
  if (!quote) return <span data-testid={testId} className={`text-[9px] text-gray-500 ${className}`}>MSN Finance · pulling…</span>;
  return (
    <span data-testid={testId} title={`Price / 1D from ${quote.source} — pulled ${fmtAsOf(quote.as_of)} (shared 30s cache)`}
      className={`text-[9px] text-gray-500 whitespace-nowrap ${className}`}>
      {quote.source} · {fmtAsOf(quote.as_of)}
    </span>
  );
}
